import { ContactFormField } from "../../types"
import CustomTextInput from "../../../../ui/atoms/CustomTextInput"
import CustomTextArea from "../../../../ui/atoms/CustomTextArea"
import CustomFileInput from "../../../../ui/atoms/CustomFileInput"
import Typography from "../../../../ui/atoms/Typography"
import React from "react"
import { useEffect, useState } from "react"
import { useStores } from "../../../stores/hooks"
import { useProvinces } from "../../../addresses/hooks/provinces"
import classes from "../ContactForm/ContactForm.module.css"
import classNames from "classnames"
import { PortableText } from "@portabletext/react"

interface ContactFormFieldsSwitcherProps {
  content: ContactFormField
  register: any
  errors: any
}

const ContactFormFieldsSwitcher = ({
  content,
  register,
  errors,
}: ContactFormFieldsSwitcherProps) => {
  if (!content) {
    return <></>
  }

  let renderedContent

  switch (content.type) {
    // Campo di testo stringa
    case "string":
      renderedContent = (
        <>
          <CustomTextInput
            type="text"
            className="my-2"
            label={content.placeholder[0].children[0].text}
            name={content.label}
            inputRef={register}
            classes={{
              input: "border-solid border-b-2 border-lightBlack bg-transparent",
            }}
          />
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )
      break
    // Campo di testo email
    case "email":
      renderedContent = (
        <>
          <CustomTextInput
            type="email"
            className="my-2"
            label={content.placeholder[0].children[0].text}
            name={content.label}
            inputRef={register}
            classes={{
              input: "border-solid border-b-2 border-lightBlack bg-transparent",
            }}
          />
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )
      break
    // Campo di testo telefono
    case "phone":
      renderedContent = (
        <>
          <CustomTextInput
            type="tel"
            className="my-2"
            label={content.placeholder[0].children[0].text}
            name={content.label}
            inputRef={register}
            classes={{
              input: "border-solid border-b-2 border-lightBlack bg-transparent",
            }}
          />
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )
      break
    // Campo di testo textarea
    case "textarea":
      renderedContent = (
        <>
          <CustomTextArea
            label={content.placeholder[0].children[0].text}
            name={content.label}
            inputRef={register}
          />
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )
      break
    // Campo di input file
    case "file":
      renderedContent = (
        <>
          <CustomFileInput
            label={content.placeholder[0].children[0].text}
            name={content.label}
            inputRef={register}
            acceptedFiles="application/pdf"
          />
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )
      break
    // Campo di input checkbox
    case "checkbox":
      renderedContent = (
        <>
          <div className="flex align-middle align-items-center">
            <input
              type="checkbox"
              name={content.label}
              ref={register}
              className="mr-2"
            />
            <div className={classNames(classes.rich)}>
              <PortableText value={content.placeholder} />
            </div>
          </div>
          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )

      break
    // Campo di input select
    case "select":
      const [selectValue, setSelectValue] = useState(content?.options[0] ?? "")
      const handleSelectChange = (value: string) => {
        setSelectValue(value)
      }

      renderedContent = (
        <>
          <Typography variant="body2" className="mb-2">
            {content.placeholder[0].children[0].text}
          </Typography>

          <select
            name={content.label}
            ref={register}
            onChange={(e: any) => {
              handleSelectChange(e.target.value)
            }}
            value={selectValue}
            className={classNames(classes.select)}
          >
            {content.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>

          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )

      break
    // Campo di input select popolato con province
    case "province":
      const provinces = useProvinces("IT")

      const [selectProvince, setSelectProvince] = useState(
        provinces.data?.provinces[0].name ?? ""
      )
      const handleProvinceChange = (value: string) => {
        setSelectProvince(value)
      }

      renderedContent = (
        <>
          <Typography variant="body2" className="mb-2">
            {content.placeholder[0].children[0].text}
          </Typography>
          <select
            name={content.label}
            ref={register}
            onChange={(e: any) => {
              handleProvinceChange(e.target.value)
            }}
            value={selectProvince}
            className={classNames(classes.select)}
          >
            {provinces.data?.provinces.map((province, index) => (
              <option key={index} value={province.name}>
                {province.name}
              </option>
            ))}
          </select>

          {errors[content.label] && (
            <Typography variant="body2" color="warning">
              {errors[content.label].message}
            </Typography>
          )}
        </>
      )

      break

    // Campo di input select popolato con ruoli (da sanity), che in caso di punto vendita mostra anche i punti vendita
    case "role":
      const stores = useStores({
        includeHidden: false,
      })

      const [store, setStore] = useState("")

      useEffect(() => {
        if (stores.data?.length > 0) {
          setStore(stores.data[0].name)
        }
      }, [stores.data])

      const handleStoreChange = (value: string) => {
        setStore(value)
      }

      // Creo preventivamente uno state per i ruoli e per gli stores.
      const [selectRole, setSelectRole] = useState(content.options[0] ?? "")
      const handleRoleChange = (value: string) => {
        setSelectRole(value)
      }

      renderedContent = (
        <>
          <div className="w-full mb-4">
            <Typography variant="body2" className="mb-2">
              {content.placeholder[0].children[0].text}
            </Typography>
            <select
              name={content.label}
              ref={register}
              onChange={(e: any) => {
                handleRoleChange(e.target.value)
              }}
              value={selectRole}
              className={classNames(classes.select)}
            >
              {content.options.map((role, index) => (
                <option key={index} value={role}>
                  {role}
                </option>
              ))}
            </select>
            {errors[content.label] && (
              <Typography variant="body2" color="warning">
                {errors[content.label].message}
              </Typography>
            )}
          </div>
          {selectRole === "Beauty Store" && (
            <>
              <Typography variant="body2" className="mb-2">
                Scegli il punto vendita in cui mandare la candidatura
              </Typography>
              <select
                name="store"
                ref={register}
                onChange={(e: any) => {
                  handleStoreChange(e.target.value)
                }}
                value={store}
                className={classNames(classes.select)}
              >
                {stores.data?.map((store, index) => (
                  <option key={index} value={store.name}>
                    {store.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </>
      )

      break

    default:
      renderedContent = (
        <Typography variant="body2" color="warning">
          Errore! Il tipo di campo non è stato riconosciuto
        </Typography>
      )
      break
  }

  return <>{renderedContent}</>
}

export default ContactFormFieldsSwitcher
